import { useRef, useState } from "react";
import { UploadSimple, Trash, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, API_ROOT } from "@/lib/api";
import { formatAuditTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

// Staff-uploaded event background image, served through the protected backend route.
export function EventHeroImageUpload({ eventId, event, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = `${API_ROOT}/staff/events/${eventId}/hero-image?v=${encodeURIComponent(
    event.hero_image_updated_at || "",
  )}`;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/staff/events/${eventId}/hero-image`, fd);
      toast.success("Event image updated.");
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/staff/events/${eventId}/hero-image`);
      toast.success("Event image removed from the portal.");
      onSaved?.();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="event-hero-image-panel">
      <Label className="eyebrow">Event background image</Label>
      <p className="mt-1 text-xs text-muted-foreground">
        Shown behind the couple's countdown on their dashboard. JPG, PNG or WebP up to 8 MB.
      </p>
      <div className="mt-3 overflow-hidden rounded-lg border border-border bg-muted">
        {event.has_hero_image ? (
          <img
            src={src}
            alt="Event background"
            className="h-40 w-full object-cover"
            data-testid="event-hero-image-preview"
          />
        ) : (
          <div
            className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground"
            data-testid="event-hero-image-empty"
          >
            <ImageIcon size={26} weight="light" />
            <p className="text-xs">No image uploaded yet</p>
          </div>
        )}
      </div>
      {event.has_hero_image && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="event-hero-image-meta">
          {event.hero_image_name || "Uploaded image"} · {formatAuditTime(event.hero_image_updated_at)}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif"
          hidden
          onChange={upload}
          data-testid="event-hero-image-input"
        />
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          data-testid="upload-event-hero-image-btn"
        >
          <UploadSimple size={16} className="mr-1" />
          {busy ? "Working…" : event.has_hero_image ? "Replace image" : "Upload image"}
        </Button>
        {event.has_hero_image && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full text-destructive"
            disabled={busy}
            onClick={remove}
            data-testid="remove-event-hero-image-btn"
          >
            <Trash size={16} className="mr-1" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
